import { useLocalization } from '@magicdoorinc/ui';
import { createSignal, createContext, useContext, createMemo, Show } from 'solid-js';
import IconArrowRight from '~/assets/images/common/arrowRight.svg?component-solid';
import IconBlog from '~/assets/images/common/blog.svg?component-solid';
import { LeaseTitle } from '~/components/leases/LeaseTitle';
import { OwnerTitle } from '~/components/owner/OwnerTitle';
import { PropertyTitle } from '~/components/properties/PropertyTitle';
import { TenantTitle } from '~/components/tenants/TenantTitle';
import { UnitTitle } from '~/components/units/UnitTitle';
import { VendorTitle } from '~/components/vendors/VendorTitle';
import { buildNavigationDestinations } from '~/config/navigationSearchIndex';
import { useCurrentCompany, usePropertyManagerPermissions } from '~/contexts/global';
import { LeaseRepository } from '~/repositories/leaseRepository';
import { OwnersRepository } from '~/repositories/ownersRepository';
import { PropertiesRepository } from '~/repositories/propertiesRepository';
import { TenantRepository } from '~/repositories/tenantRepository';
import { tutorialRepository } from '~/repositories/tutorialRepository';
import { UnitRepository } from '~/repositories/unitRepository';
import { VendorRepository } from '~/repositories/vendorRepository';
import { IS_HOA_DEMO_MODE } from '~/utils/hoaDemoMode';
import { matchNavigationDestinations } from '~/utils/navigationSearch';
import type { JSX } from 'solid-js';
import type { NavigationDestinationSource } from '~/config/navigationSearchIndex';
import type { VendorsDto } from '~/swagger/maintenance/Api';
import type {
  HydratedLeaseDtoPaginationDto,
  HydratedPropertyPaginationDto,
  HydratedTenantListPaginationDto,
  HydratedUnitPaginationDto,
  HydratedOwnerListPaginationDto,
} from '~/swagger/portal/Api';
import type { TutorialDto } from '~/types/tutorial';

export interface SearchResult {
  id: string;
  type: 'navigation' | 'lease' | 'property' | 'unit' | 'tenant' | 'owner' | 'vendor' | 'tutorial';
  renderItem: () => JSX.Element;
  response: any;
}

export interface SearchResults {
  navigation: SearchResult[];
  leases: SearchResult[];
  properties: SearchResult[];
  units: SearchResult[];
  tenants: SearchResult[];
  owners: SearchResult[];
  vendors: SearchResult[];
  tutorials: SearchResult[];
}

interface SearchContextProps {
  searchResults: () => SearchResults;
  performSearch: (term: string | undefined) => void;
  applyNavigationResults: (term: string | undefined) => void;
  resetSearchResults: () => void;
  loadMore: (type: string) => Promise<void>;
  hasMore: (type: string) => boolean;
  isLoading: () => boolean;
}

const SearchContext = createContext<SearchContextProps>();

const leaseRepo = new LeaseRepository();
const propertyRepo = new PropertiesRepository();
const unitRepo = new UnitRepository();
const tenantRepo = new TenantRepository();
const ownerRepo = new OwnersRepository();
const vendorRepo = new VendorRepository();

const commonTitleClass = 'text-sm text-gray-800 h-fit flex items-center';

const NAVIGATION_RESULT_LIMIT = 8;

const NAVIGATION_SOURCE_BADGES: Record<NavigationDestinationSource, { labelKey: string; class: string }> = {
  menu: { labelKey: 'Page', class: 'bg-primary-light text-primary' },
  settings: { labelKey: 'Settings', class: 'bg-warning-light text-warning' },
  report: { labelKey: 'Report', class: 'bg-success-light text-success' },
};

const NavigationResultItem = (props: { label: string; group?: string; source: NavigationDestinationSource }) => {
  const { t } = useLocalization();
  const badge = () => NAVIGATION_SOURCE_BADGES[props.source];

  return (
    <div class="flex items-center gap-2">
      <IconArrowRight class="h-4 w-[18px] shrink-0 text-text-03" />
      <span class="truncate text-sm text-text-01">{props.label}</span>
      <span class={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${badge().class}`}>{t(badge().labelKey)}</span>
      <Show when={props.group}>
        <span class="truncate text-xs text-text-03">{props.group}</span>
      </Show>
    </div>
  );
};

const formatCategoryName = (category: string): string => {
  if (IS_HOA_DEMO_MODE) {
    switch (category) {
      case 'PropertyManager':
        return 'HOA Manager';
      case 'Property':
        return 'HOA';
      case 'Properties':
        return 'HOAs';
      case 'Tenant':
        return 'Member';
      case 'Tenants':
        return 'Members';
      case 'Owner':
        return 'Board Member';
      case 'Owners':
        return 'Board Members';
      case 'Lease':
        return 'Member Account';
      case 'Leases':
        return 'Member Accounts';
    }
  }
  if (category === 'PropertyManager') {
    return 'Property Manager';
  }
  return category;
};

export const SearchProvider = (props: { children?: any }) => {
  const { t } = useLocalization();
  const { checkFeature } = useCurrentCompany();
  const { menuAccess, hasPermission } = usePropertyManagerPermissions();

  const navigationDestinations = createMemo(() => {
    const accessible = new Set(menuAccess());
    return buildNavigationDestinations(checkFeature)
      .filter((destination) => !destination.menuAccessKey || accessible.has(destination.menuAccessKey))
      .filter((destination) => !destination.permission || hasPermission(destination.permission.resource, destination.permission.action));
  });

  const [searchTerm, setSearchTerm] = createSignal<string | undefined>(undefined);
  const [searchResults, setSearchResults] = createSignal<SearchResults>({
    navigation: [],
    leases: [],
    properties: [],
    units: [],
    tenants: [],
    owners: [],
    vendors: [],
    tutorials: [],
  });
  const [page, setPage] = createSignal<Record<string, number>>({
    leases: 1,
    properties: 1,
    units: 1,
    tenants: 1,
    owners: 1,
    vendors: 1,
    tutorials: 1,
  });
  const [hasMoreResults, setHasMoreResults] = createSignal<Record<string, boolean>>({
    navigation: false,
    leases: true,
    properties: true,
    units: true,
    tenants: true,
    owners: true,
    vendors: true,
    tutorials: false,
  });
  const [isLoading, setIsLoading] = createSignal<boolean>(false);

  const calculateHasMore = (result: { currentPage: number; totalPages: number }) => {
    return result.currentPage < result.totalPages;
  };

  const buildNavigationResults = (term: string | undefined): SearchResult[] => {
    if (!term) return [];
    return matchNavigationDestinations(navigationDestinations(), term, t, NAVIGATION_RESULT_LIMIT).map((match) => ({
      id: match.destination.id,
      type: 'navigation' as const,
      renderItem: () => <NavigationResultItem label={match.label} group={match.group} source={match.destination.source} />,
      response: match.destination,
    }));
  };

  const applyNavigationResults = (term: string | undefined) => {
    setSearchTerm(term);
    setSearchResults((prev) => ({ ...prev, navigation: buildNavigationResults(term) }));
  };

  const performSearch = async (term: string | undefined) => {
    if (!term) return;
    setSearchTerm(term);
    setIsLoading(true);
    try {
      const results = await Promise.all([
        leaseRepo.getLeases({ pageSize: 20, search: term, page: 1, ended: false }),
        propertyRepo.getProperties({ pageSize: 20, search: term, page: 1, active: true }),
        unitRepo.getUnits({ pageSize: 20, search: term, page: 1, active: true }),
        tenantRepo.getTenants({ pageSize: 20, search: term, page: 1, active: true }),
        ownerRepo.getOwners({ pageSize: 20, search: term, page: 1, active: true }),
        vendorRepo.getVendors({ pageSize: 20, search: term, page: 1 }),
        tutorialRepository.searchTutorials({ query: term }).catch(() => [] as TutorialDto[]),
      ]);

      setHasMoreResults({
        leases: calculateHasMore(results[0]),
        properties: calculateHasMore(results[1]),
        units: calculateHasMore(results[2]),
        tenants: calculateHasMore(results[3]),
        owners: calculateHasMore(results[4]),
        vendors: calculateHasMore(results[5]),
        tutorials: false,
      });

      setSearchResults({
        navigation: buildNavigationResults(term),
        leases: results[0].items.map(mapSearchResult('lease')),
        properties: results[1].items.map(mapSearchResult('property')),
        units: results[2].items.map(mapSearchResult('unit')),
        tenants: results[3].items.map(mapSearchResult('tenant')),
        owners: results[4].items.map(mapSearchResult('owner')),
        vendors: results[5].items.map(mapSearchResult('vendor')),
        tutorials: (Array.isArray(results[6]) ? results[6] : []).map(mapSearchResult('tutorial')),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const mapSearchResult =
    (type: SearchResult['type']) =>
    (item: any): SearchResult => ({
      id: item.id,
      type,
      renderItem: () => {
        return (
          <>
            <Show when={type === 'lease'}>
              <LeaseTitle
                feedbackIconReadonly
                lease={item}
                class={commonTitleClass}
                showTenants
                customLinks={[
                  { displayName: 'Chat', href: `/leasing/leases/${item.id}/communications` },
                  { displayName: 'Transactions', href: `/leasing/leases/${item.id}/transactions` },
                  { displayName: 'Add maintenance request', href: `/maintenance/maintenance-request/add?leaseId=${item.id}` },
                  { displayName: 'Add work order', href: `/maintenance/work-orders/add?leaseId=${item.id}` },
                  { displayName: 'Add a charge', href: `/leasing/leases/${item.id}/transactions/add-charge` },
                ]}
              />
            </Show>
            <Show when={type === 'property'}>
              <PropertyTitle feedbackIconReadonly property={item} class={commonTitleClass} />
            </Show>
            <Show when={type === 'unit'}>
              <UnitTitle feedbackIconReadonly unit={item} class={commonTitleClass} />
            </Show>
            <Show when={type === 'tenant'}>
              <TenantTitle
                feedbackIconReadonly
                tenant={item}
                class={commonTitleClass}
                customLinks={[{ displayName: 'Chat', href: `/users/tenants/${item.id}/communications` }]}
              />
            </Show>
            <Show when={type === 'owner'}>
              <OwnerTitle
                owner={item}
                class={commonTitleClass}
                customLinks={[{ displayName: 'Chat', href: `/users/owners/${item.id}/communications` }]}
              />
            </Show>
            <Show when={type === 'vendor'}>
              <VendorTitle
                feedbackIconReadonly
                vendor={item}
                class={commonTitleClass}
                customLinks={[{ displayName: 'Chat', href: `/maintenance/vendors/${item.id}/communications` }]}
              />
            </Show>
            <Show when={type === 'tutorial'}>
              <div class="flex items-start gap-3">
                <div class="bg-primary-alpha flex h-12 w-16 shrink-0 items-center justify-center rounded">
                  <Show
                    when={item.linkToTextGuide}
                    fallback={
                      <img
                        src={`https://img.youtube.com/vi/${item.youTubeVideoId}/default.jpg`}
                        alt={item.name}
                        class="h-12 w-16 rounded object-cover"
                      />
                    }>
                    <IconBlog aria-hidden="true" class="size-6" />
                  </Show>
                </div>
                <div class="flex-1">
                  <div class="mb-1 flex items-center gap-2">
                    <span class="text-sm font-medium text-text-01">{item.name}</span>
                    <span class="bg-primary-alpha rounded px-1.5 py-0.5 text-xs text-primary">{formatCategoryName(item.category)}</span>
                  </div>
                  <p class="line-clamp-2 text-xs text-text-03">{item.description}</p>
                </div>
              </div>
            </Show>
          </>
        );
      },
      response: item,
    });

  const loadMore = async (type: string) => {
    const currentPage = page()[type];
    const nextPage = currentPage + 1;
    let newResults:
      | HydratedLeaseDtoPaginationDto
      | HydratedPropertyPaginationDto
      | HydratedUnitPaginationDto
      | HydratedTenantListPaginationDto
      | HydratedOwnerListPaginationDto
      | VendorsDto;

    setIsLoading(true);

    try {
      switch (type) {
        case 'leases':
          newResults = await leaseRepo.getLeases({ pageSize: 20, search: searchTerm(), page: nextPage, ended: false });
          break;
        case 'properties':
          newResults = await propertyRepo.getProperties({ pageSize: 20, search: searchTerm(), page: nextPage, active: true });
          break;
        case 'units':
          newResults = await unitRepo.getUnits({ pageSize: 20, search: searchTerm(), page: nextPage, active: true });
          break;
        case 'tenants':
          newResults = await tenantRepo.getTenants({ pageSize: 20, search: searchTerm(), page: nextPage, active: true });
          break;
        case 'owners':
          newResults = await ownerRepo.getOwners({ pageSize: 20, search: searchTerm(), page: nextPage, active: true });
          break;
        case 'vendors':
          newResults = await vendorRepo.getVendors({ pageSize: 20, search: searchTerm(), page: nextPage });
          break;
        case 'tutorials':
          return;

        default:
          return;
      }

      setPage({ ...page(), [type]: nextPage });
      setHasMoreResults({ ...hasMoreResults(), [type]: calculateHasMore(newResults) });

      const singularType = getSingularType(type);

      setSearchResults((prev) => ({
        ...prev,
        [type]: [...(prev[type] || []), ...newResults.items.map(mapSearchResult(singularType as SearchResult['type']))],
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const getSingularType = (type: string): string => {
    switch (type) {
      case 'properties':
        return 'property';
      case 'leases':
        return 'lease';
      case 'units':
        return 'unit';
      case 'tenants':
        return 'tenant';
      case 'owners':
        return 'owner';
      case 'vendors':
        return 'vendor';
      case 'tutorials':
        return 'tutorial';
      default:
        return type.slice(0, -1);
    }
  };

  const resetSearchResults = () => {
    setSearchTerm('');
    setSearchResults({
      navigation: [],
      leases: [],
      properties: [],
      units: [],
      tenants: [],
      owners: [],
      vendors: [],
      tutorials: [],
    });
    setPage({
      leases: 1,
      properties: 1,
      units: 1,
      tenants: 1,
      owners: 1,
      vendors: 1,
      tutorials: 1,
    });
    setHasMoreResults({
      navigation: false,
      leases: true,
      properties: true,
      units: true,
      tenants: true,
      owners: true,
      vendors: true,
      tutorials: false,
    });
  };

  const hasMore = (type: string) => {
    return hasMoreResults()[type];
  };

  return (
    <SearchContext.Provider
      value={{
        searchResults,
        performSearch,
        applyNavigationResults,
        resetSearchResults,
        loadMore,
        hasMore,
        isLoading,
      }}>
      {props.children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
